import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../api/httpClient";
import { getApiBaseUrl } from "../../data/authStorage";
import DataTable from "../../components/table/data-table";
import { InviteModal, SuccessModal } from "../../components/modal";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";

const PERMISSIONS_CACHE_KEY = "cehr_company_permissions_v1";

const Permissions = () => {
  const cachedData = useMemo(() => readSessionCache(PERMISSIONS_CACHE_KEY, { permissions: [], positions: [] }), []);
  const [openPermissionModal, setOpenPermissionModal] = useState(false);
  const [permissions, setPermissions] = useState(cachedData?.permissions || []);
  const [positions, setPositions] = useState(cachedData?.positions || []);
  const [isLoading, setIsLoading] = useState(!(cachedData?.permissions?.length || cachedData?.positions?.length));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [opensuccessModal, setOpensuccessModal] = useState(false);
  const [permissionName, setPermissionName] = useState("");
  const [description, setDescription] = useState("");
  const [component, setComponent] = useState("");
  const [selectedPositionId, setSelectedPositionId] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();

  const loadPermissions = async () => {
    if (permissions.length === 0 && positions.length === 0) {
      setIsLoading(true);
    }
    try {
      const response = await axios.get(`${getApiBaseUrl()}/organizations/permissions`);
      if (response.data && response.data.permissions) {
        const nextPermissions = response.data.permissions;
        setPermissions(nextPermissions);
        writeSessionCache(PERMISSIONS_CACHE_KEY, {
          permissions: nextPermissions,
          positions,
        });
      }
    } catch (error) {
      console.error("Error loading permissions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadPositions = async () => {
    try {
      const response = await axios.get(`${getApiBaseUrl()}/organizations/positions`);
      if (response.data && response.data.positions) {
        const nextPositions = response.data.positions;
        setPositions(nextPositions);
        writeSessionCache(PERMISSIONS_CACHE_KEY, {
          permissions,
          positions: nextPositions,
        });
      }
    } catch (error) {
      console.error("Error loading positions:", error);
    }
  };

  useEffect(() => {
    const hydrate = () => {
      if (cachedData?.permissions?.length || cachedData?.positions?.length) {
        setPermissions(cachedData.permissions || []);
        setPositions(cachedData.positions || []);
      }
      loadPermissions();
      loadPositions();
    };

    hydrate();

    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadPermissions();
        loadPositions();
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, []);

  const openModal = () => {
    setErrorMessage("");
    setPermissionName("");
    setDescription("");
    setComponent("");
    setSelectedPositionId("");
    setOpenPermissionModal(true);
  };

  const closeModal = () => {
    setOpenPermissionModal(false);
  };

  const createHandleSuccess = async (ev) => {
    ev.preventDefault();
    setErrorMessage("");
    setIsSubmitting(true);

    if (!selectedPositionId || !permissionName.trim() || !description.trim() || !component.trim()) {
      setErrorMessage("All fields are required");
      setIsSubmitting(false);
      return;
    }

    try {
      const payload = {
        positionId: selectedPositionId,
        permissionName: permissionName.trim(),
        description: description.trim(),
        component: component.trim(),
      };

      await axios.post(`${getApiBaseUrl()}/organizations/permissions`, payload);
      setOpensuccessModal(true);
      closeModal();
      await loadPermissions();
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to create permission. Please try again.";
      setErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const headings = ["Permission", "Component", "Position", "Actions"];
  const columns = ["permissionName", "component", "positionName"];

  return (
    <div className="dashboard-enter">
      <PageIntroNav
        breadcrumbs={[
          { label: "Company Settings", path: "/company-settings" },
          { label: "Permissions" },
        ]}
        title="Permissions"
        subtitle="Assign permissions to positions and control what each can access."
        fallbackPath="/company-settings"
        backLabel="Back to Company Settings"
        actions={(
          <button
            onClick={openModal}
            className="bg-[#1234CD] px-6 py-2 rounded-xl text-white"
          >
            Add new
          </button>
        )}
      />

      <DataTable
        title=""
        headings={headings}
        columns={columns}
        data={permissions}
        showEmptyState={!isLoading}
        emptyMessage="No permissions found. Create one to get started."
        onView={(row) => {
          navigate("/company-settings/permissionview-details", {
            state: { permissionId: row.id },
          });
        }}
      />

      <InviteModal
        isOpen={openPermissionModal}
        onClose={closeModal}
      >
        <div className="my-8">
          <div>
            <p className=" text-2xl font-semibold text-black font-Outfit">Create Permission</p>
          </div>
          <form
            className="my-12"
            onSubmit={createHandleSuccess}
          >
            {errorMessage && (
              <p className="mb-4 text-sm text-red-600 dashboard-fade-in" role="alert">
                {errorMessage}
              </p>
            )}

            <div>
              <label
                htmlFor="position-select"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh"
              >
                Position
              </label>
              <select
                id="position-select"
                value={selectedPositionId}
                onChange={(ev) => setSelectedPositionId(ev.target.value)}
                className="w-full p-2 text-start mb-2 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              >
                <option value="">Select a position</option>
                {positions.map((position) => (
                  <option key={position.id} value={position.id}>
                    {position.positionName || position.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="permission-name"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh"
              >
                Permission Name
              </label>
              <input
                type="text"
                id="permission-name"
                placeholder="e.g. View Reports"
                value={permissionName}
                onChange={(ev) => setPermissionName(ev.target.value)}
                className="w-full p-2 text-center mb-2 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div>
              <label
                htmlFor="permission-description"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh"
              >
                Description
              </label>
              <textarea
                id="permission-description"
                placeholder="Describe what this permission allows"
                value={description}
                onChange={(ev) => setDescription(ev.target.value)}
                className="w-full p-2 text-start mb-2 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px] resize-none"
                rows="3"
              />
            </div>

            <div>
              <label
                htmlFor="permission-component"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh"
              >
                Component/Module
              </label>
              <input
                type="text"
                id="permission-component"
                placeholder="e.g. Dashboard, Reports, Settings"
                value={component}
                onChange={(ev) => setComponent(ev.target.value)}
                className="w-full p-2 text-center mb-2 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div className="flex gap-4 mt-8">
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-[#1234CD] hover:bg-blue-700 disabled:opacity-50 px-6 py-2 rounded-xl text-white font-medium dashboard-button-hover"
              >
                {isSubmitting ? "Creating..." : "Create Permission"}
              </button>
              <button
                type="button"
                onClick={closeModal}
                className="flex-1 border-2 border-gray-300 px-6 py-2 rounded-xl text-gray-700 font-medium hover:bg-gray-50 dashboard-button-hover"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </InviteModal>

      <SuccessModal
        isOpen={opensuccessModal}
        onClose={() => setOpensuccessModal(false)}
      >
        <div className="text-center">
          <p className="text-lg font-semibold text-green-600">Permission created successfully!</p>
        </div>
      </SuccessModal>

      {isLoading && permissions.length === 0 ? <FetchLoadingState message="Loading permissions..." /> : null}
    </div>
  );
};

export default Permissions;
